import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Zap, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useVenue } from '../../contexts/VenueContext';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";
const SEPA_FEE = 0.80;

const cardStyle = {
  ...hostCardStyle,
  width: 'calc(100% - 32px)',
  maxWidth: 360,
  padding: '18px 18px',
  boxSizing: 'border-box',
};

const isValidIban = (raw) => {
  const cleaned = (raw || '').replace(/\s+/g, '').toUpperCase();
  if (cleaned.length < 15 || cleaned.length > 34) return false;
  return /^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(cleaned);
};

const formatIban = (raw) => (raw || '').replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

export default function VenueConnectVivaPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const location = useLocation();
  const { activeVenueAppId } = useVenue();
  const [vivaIban, setVivaIban] = useState('');
  const [holderName, setHolderName] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentMethod, setCurrentMethod] = useState(location.state?.previousMethod ?? null);

  const getToken = useCallback(() => localStorage.getItem('token'), []);

  useEffect(() => {
    if (!activeVenueAppId) return;
    axios.get(`${API}/venue/payout-method/${activeVenueAppId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then((res) => {
      setCurrentMethod(res.data?.payout_method);
      if (res.data?.viva_iban_holder_name) setHolderName(res.data.viva_iban_holder_name);
    }).catch(() => {});
  }, [activeVenueAppId, getToken]);

  const valid = isValidIban(vivaIban) && holderName.trim().length >= 2;
  const wasIbanBefore = currentMethod === 'iban';

  const handleSave = async () => {
    if (!valid || !activeVenueAppId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/venue/payout-method/viva`, {
        venue_id: activeVenueAppId,
        viva_iban: vivaIban.replace(/\s+/g, ''),
        viva_iban_holder_name: holderName.trim(),
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(wasIbanBefore ? `€${SEPA_FEE.toFixed(2)} per-transaction fee removed!` : 'Viva Wallet connected');
      navigate('/venue/settings/payouts');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect Viva Wallet');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-shell" data-testid="venue-connect-viva-page" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40,
    }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <button data-testid="venue-connect-viva-back" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
      </div>

      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-connect-viva-logo" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Zap size={20} color="#16A34A" strokeWidth={2.4} />
        </div>
        <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Connect Viva Wallet</h2>
      </div>

      {wasIbanBefore && (
        <div data-testid="venue-connect-viva-savings-banner" style={{
          width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 14,
          padding: '12px 14px', borderRadius: 14,
          background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)',
        }}>
          <p style={{ fontFamily: font, fontSize: 15, color: 'var(--status-success-text)', margin: 0, lineHeight: 1.3 }}>
            <CheckCircle2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
            Switching from IBAN to Viva will <strong>remove the €{SEPA_FEE.toFixed(2)} fee</strong> on every transaction.
          </p>
        </div>
      )}

      <div style={{ ...cardStyle, marginTop: 16, padding: '18px' }}>
        <label style={{ display: 'block', fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          Viva Wallet IBAN
        </label>
        <input
          data-testid="venue-connect-viva-iban-input"
          type="text"
          value={formatIban(vivaIban)}
          onChange={(e) => setVivaIban(e.target.value)}
          placeholder="GR16 0110 1250 0000 0001 2300 695"
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 12,
            border: '1px solid var(--card-border)', fontFamily: 'monospace',
            fontSize: 15, boxSizing: 'border-box', textTransform: 'uppercase',
          }}
        />
        {vivaIban && !isValidIban(vivaIban) && (
          <p data-testid="venue-connect-viva-error" style={{ fontFamily: font, fontSize: 13, color: 'var(--status-danger-text)', margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertCircle size={14} /> Doesn&apos;t look like a valid IBAN — check the country prefix and length.
          </p>
        )}
        <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: '10px 0 0', lineHeight: 1.35 }}>
          Find your Viva Wallet IBAN in your Viva portal → Wallets → your EUR wallet → IBAN.
        </p>

        <label style={{ display: 'block', fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 14, marginBottom: 8 }}>
          Account holder name
        </label>
        <input
          data-testid="venue-connect-viva-holder-input"
          type="text"
          value={holderName}
          onChange={(e) => setHolderName(e.target.value)}
          placeholder="Your business name or full legal name"
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 12,
            border: '1px solid var(--card-border)', fontFamily: font,
            fontSize: 15, boxSizing: 'border-box',
          }}
        />
      </div>

      <button
        data-testid="venue-connect-viva-submit"
        onClick={handleSave}
        disabled={!valid || saving}
        style={{
          width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 16,
          padding: '14px 0', borderRadius: 16, border: 'none',
          background: !valid || saving ? 'var(--status-success-surface)' : '#16A34A',
          color: '#FFFFFF', fontFamily: font, fontSize: 20, fontWeight: 700,
          cursor: !valid || saving ? 'not-allowed' : 'pointer',
        }}
      >
        {saving ? 'Connecting…' : 'Connect Viva Wallet'}
      </button>

      <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', textAlign: 'center', maxWidth: 320, marginTop: 14, lineHeight: 1.3 }}>
        Don&apos;t have a Viva Wallet account?{' '}
        <a
          data-testid="venue-connect-viva-signup-link"
          href="https://www.viva.com/en-eu/business"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--accent-purple)', textDecoration: 'underline' }}
        >
          Create one free
        </a>
      </p>
    </div>
  );
}
